/**
 * Mantenimiento Facturas x Mayor — migración web de ares9720.
 *
 * Lista las facturas de ventas al por mayor por rango de fechas (y cliente
 * opcional) y permite:
 *   - Abrir el DETALLE UNIFICADO de la factura (facturadetallexmayor.js):
 *     líneas con historial de devueltos, plan de pago, modificar precios,
 *     devolver motor consignado y aplicar cobros, todo en una pantalla.
 *   - Anular una factura vigente sin pagos (vplicado = 0), o una al contado
 *     (nace con vplicado = total y sin recibo; el backend rechaza si tiene
 *     cobros en pagoscli): borra sus cuotas y devuelve los vehículos
 *     facturados a disponible.
 *   - Imprimir el comprobante de la factura con sus líneas (chasis) y, si es
 *     a crédito, el plan de cuotas.
 *
 * Con `soloConsignadas` la usa el segmento de Consignaciones para listar solo
 * las facturas consignadas (modelo anterior: deuda por el total + devolución).
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useGlobalMDAlert } from "context";
import {
  FP,
  PageHeader,
  fpInputSx,
  fpDataGridSx,
  RazonCancelacionField,
  TooltipRazonCancelacion,
} from "components/financepro";
import { useApi } from "lib/api";
import FacturaDetalleXmayor from "modules/ventasxmayor/facturadetallexmayor";
import { fmtMoney, monedaLabel, imprimirFactura } from "modules/ventasxmayor/impresionxmayor";

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function Vacio({ texto }) {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>{texto}</Typography>
    </Stack>
  );
}

function NoRowsOverlay() {
  return <Vacio texto="Sin facturas en el rango" />;
}

function NoRowsConsignadas() {
  return <Vacio texto="Sin facturas consignadas en el rango" />;
}

export default function MantenimientoFacturasXmayor({
  embedded = false,
  refreshToken = 0,
  // El segmento de Consignaciones reutiliza esta pantalla para las facturas
  // consignadas del modelo anterior (consignado='S', con deuda por el total y
  // devolución). El filtro es en cliente porque el listado ya trae la marca.
  soloConsignadas = false,
} = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Facturas emitidas desde conduces consignados: quedan fuera de este listado
  // (se ven dentro de su conduce), así que se informa el hueco para que el total
  // no parezca un descuadre contra antigüedad de saldo.
  const [conduces, setConduces] = useState(null);

  const cargarClientes = async () => {
    const { ok, data } = await post("/api/xmayor_clientes", {});
    if (ok) setClientes(data.clientes || []);
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const body = { fechadesde: fecha1, fechahasta: fecha2 };
      if (cliente) body.cliente = cliente.id;
      const { ok, data } = await post("/api/xmayor_facturas_listar", body);
      if (ok) {
        const facturas = (data.facturas || []).map((r) => ({ ...r, id: r.id }));
        setRows(soloConsignadas ? facturas.filter((r) => r.consignada) : facturas);
        setConduces(!soloConsignadas && Number(data.conduces?.facturas) > 0 ? data.conduces : null);
      } else {
        showAlert({ message: data.Error || "No se pudieron listar las facturas", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarClientes();
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // ---- Anulación ----
  const [target, setTarget] = useState(null);
  const [razon, setRazon] = useState("");
  // Cada factura que se abre para cancelar empieza sin razón.
  useEffect(() => setRazon(""), [target]);
  const [anulando, setAnulando] = useState(false);

  const anular = async () => {
    setAnulando(true);
    try {
      const { ok, data } = await post("/api/xmayor_factura_anular", { id: target.id, razon });
      if (ok) {
        showAlert({ message: data.mensajeok || "Factura anulada", color: "success" });
        setTarget(null);
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo anular la factura", color: "error" });
      }
    } finally {
      setAnulando(false);
    }
  };

  // ---- Impresión ----
  const [imprimiendo, setImprimiendo] = useState(0);

  const imprimir = async (row) => {
    setImprimiendo(row.id);
    try {
      const { ok, data } = await post("/api/xmayor_factura_detalle", { id: row.id });
      if (ok) {
        imprimirFactura(data);
      } else {
        showAlert({ message: data.Error || "No se pudo cargar la factura", color: "error" });
      }
    } finally {
      setImprimiendo(0);
    }
  };

  // El contado nace saldado (vplicado = total) sin recibo de cobro, así que
  // vplicado no sirve para saber si tiene pagos: se deja anular y el backend
  // rechaza si encuentra cobros en pagoscli.
  const esContado = (row) =>
    String(row?.tipofactura || "")
      .trim()
      .toLowerCase() === "contado";
  const puedeAnular = (row) => !row.anulada && (esContado(row) || Number(row.vplicado) === 0);

  // ---- Detalle unificado ----
  const [detalleId, setDetalleId] = useState(null);

  const estadoChip = (row) => {
    if (row.anulada) {
      if (Number(row.devueltos) > 0) {
        return (
          <Chip
            size="small"
            label="Devuelta"
            sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
          />
        );
      }
      return (
        <TooltipRazonCancelacion razon={row.razonanula}>
          <Chip
            size="small"
            label="Anulada"
            sx={{ bgcolor: "#FFEBEE", color: "#C62828", fontWeight: 700 }}
          />
        </TooltipRazonCancelacion>
      );
    }
    if (row.status === "C") {
      return (
        <Chip
          size="small"
          label="Saldada"
          sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }}
        />
      );
    }
    return (
      <Chip
        size="small"
        label="Vigente"
        sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
      />
    );
  };

  const columns = [
    { field: "id", headerName: "Fact#", width: 80 },
    { field: "fecha", headerName: "Fecha", width: 110 },
    { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 180 },
    {
      field: "tipofactura",
      headerName: "Tipo",
      width: 170,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.6} alignItems="center">
          <span>{params.row.tipofactura}</span>
          {params.row.consignada ? (
            <Chip
              size="small"
              label="Consignada"
              sx={{ bgcolor: "#EDE7F6", color: "#4527A0", fontWeight: 700 }}
            />
          ) : null}
        </Stack>
      ),
    },
    {
      field: "cantidad",
      headerName: "Chasis",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        Number(params.row.devueltos) > 0
          ? `${params.row.cantidad} (${params.row.devueltos} dev.)`
          : params.row.cantidad,
    },
    {
      field: "valor",
      headerName: "Valor",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "vplicado",
      headerName: "Pagado",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "balance",
      headerName: "Balance",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => estadoChip(params.row),
    },
    {
      field: "acciones",
      headerName: "",
      width: 330,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8}>
          <Button
            size="small"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setDetalleId(params.row.id)}
            sx={btnOutline(FP.color.accentBlue)}
          >
            Detalle
          </Button>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setTarget(params.row)}
            disabled={!puedeAnular(params.row)}
            sx={btnOutline(FP.color.danger)}
          >
            Anular
          </Button>
          <Button
            size="small"
            startIcon={
              imprimiendo === params.row.id ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <PrintOutlinedIcon />
              )
            }
            onClick={() => imprimir(params.row)}
            disabled={imprimiendo === params.row.id}
            sx={btnOutline(FP.color.navy)}
          >
            Imprimir
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box
      component={embedded ? "section" : "main"}
      sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      {!embedded && (
        <PageHeader
          title="Mantenimiento Facturas x Mayor"
          subtitle="Consulta, anula e imprime las facturas de ventas al por mayor"
        />
      )}
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Autocomplete
                autoHighlight
                options={clientes}
                value={cliente}
                getOptionLabel={(o) => (typeof o === "string" ? o : o.descrip || "")}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                onChange={(_, v) => setCliente(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Cliente (opcional)" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={2} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={cargar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
            >
              <Typography
                sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
              >
                {rows.length} {soloConsignadas ? "facturas consignadas" : "facturas"}
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: soloConsignadas ? NoRowsConsignadas : NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
          {conduces && (
            <Typography sx={{ mt: 1.2, fontSize: "0.76rem", color: FP.color.textMuted }}>
              Además hay {conduces.facturas} factura(s) por {fmtMoney(conduces.total)} emitidas
              desde conduces consignados; se consultan dentro de su conduce y no salen en este
              listado, pero sí cuentan en antigüedad de saldo y estado de cuenta.
            </Typography>
          )}
        </Box>
      </Box>

      {/* Confirmación de anulación */}
      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.danger }}>Anular factura</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            Factura <b>{target?.id}</b> de <b>{target?.cliente}</b> por{" "}
            {monedaLabel(target?.moneda)} <b>{fmtMoney(target?.valor)}</b>.{" "}
            {esContado(target)
              ? "Es una venta al contado: la anulación saca el monto de la venta y devuelve los vehículos facturados a disponible. El comprobante fiscal emitido no se reporta automáticamente."
              : "La anulación borra las cuotas de la factura y devuelve los vehículos facturados a disponible."}{" "}
            ¿Desea continuar?
          </DialogContentText>
          <RazonCancelacionField value={razon} onChange={setRazon} disabled={anulando} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTarget(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              anulando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={anular}
            disabled={anulando}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, anular
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detalle unificado de la factura */}
      <Dialog fullScreen open={!!detalleId} onClose={() => setDetalleId(null)}>
        {detalleId && (
          <FacturaDetalleXmayor
            facturaId={detalleId}
            onExit={() => {
              setDetalleId(null);
              cargar();
            }}
            onChanged={cargar}
          />
        )}
      </Dialog>
    </Box>
  );
}
